import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-migration";
const TITLE = "Shopify Migration: Ablauf, Kosten, SEO-Sicherheit";
const DESCRIPTION =
  "Shop-Migration zu Shopify ohne Sichtbarkeitsverlust: Zeitplan in Wochen, Kostenrahmen, Weiterleitungsplan und die Punkte, an denen Migrationen typischerweise scheitern.";

const faqs = [
  {
    q: "Wie lange dauert eine Shopify Migration?",
    a: "Bei bis zu 1.000 Produkten drei bis sechs Wochen. Entscheidend ist weniger die Produktzahl als die Zahl der Sonderfälle: Varianten, Preislogik, Abos und Schnittstellen.",
  },
  {
    q: "Was kostet eine Migration zu Shopify?",
    a: "Für kleinere Shops 4.000 bis 8.000 € netto, für mittlere Sortimente mit Schnittstellen 8.000 bis 20.000 €. Reine Datenübernahme ohne neues Theme ist deutlich günstiger.",
  },
  {
    q: "Verliere ich bei der Migration Google-Rankings?",
    a: "Nicht, wenn jede alte URL per 301 auf ihr neues Ziel weitergeleitet wird und die Seitenstruktur erhalten bleibt. Übliche Schwankungen dauern vier bis acht Wochen, danach liegt die Sichtbarkeit meist wieder auf oder über dem Ausgangsniveau.",
  },
  {
    q: "Welche Daten lassen sich übernehmen?",
    a: "Produkte inklusive Varianten und Bildern, Kollektionen, Kundenkonten, Bestellhistorie, Rabattcodes und Inhaltsseiten. Passwörter lassen sich nicht übernehmen; Kunden setzen sie beim ersten Login neu.",
  },
  {
    q: "Wann ist der beste Zeitpunkt für eine Migration?",
    a: "Außerhalb der Hochsaison und nicht direkt vor einer großen Kampagne. Für die meisten Händler im DACH-Raum sind Februar bis Mai und September die ruhigsten Fenster.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-migration")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Migration"
      title="Shopify Migration: umziehen, ohne Umsatz zu verlieren"
      intro="Eine Migration ist kein Designprojekt, sondern ein Risikoprojekt. Wer die Reihenfolge einhält, verliert weder Rankings noch Bestellungen, und nutzt den Umzug, um die Kaufabschlussrate anzuheben."
      source="ratgeber-shopify-migration"
      sections={[
        {
          h: "Woche 1: Bestandsaufnahme",
          p: [
            "Vor dem ersten Handgriff steht die Liste: alle URLs mit Sichtbarkeit und Umsatz, alle Sonderfälle im Sortiment, alle angebundenen Systeme. Diese Liste bestimmt Aufwand und Reihenfolge, nicht das Wunschdesign.",
          ],
        },
        {
          h: "Woche 2 bis 4: Aufbau und Datenübernahme",
          p: [
            "Produkte, Varianten, Kollektionen und Kunden werden übertragen und stichprobenartig geprüft. Parallel entsteht das Theme, Produktseite, Warenkorb und Kaufabschluss werden bewusst neu strukturiert, statt das alte Layout nachzubauen.",
          ],
        },
        {
          h: "Der Weiterleitungsplan",
          p: [
            "Hier scheitern die meisten Migrationen. Jede alte URL braucht ein 301-Ziel: Produkte, Kategorien, Blogbeiträge, Filterseiten mit Rankings, sogar alte PDF-Dateien.",
          ],
          list: [
            "Vollständigen URL-Export aus dem Altsystem und aus der Search Console ziehen",
            "Jede URL einem neuen Ziel zuordnen, keine Sammelweiterleitung auf die Startseite",
            "Weiterleitungen vor dem Livegang testen, nicht danach",
            "Nach dem Umzug wöchentlich Crawling-Fehler kontrollieren",
          ],
        },
        {
          h: "Livegang und die ersten vier Wochen",
          p: [
            "Der Umzug findet an einem umsatzschwachen Wochentag statt. Danach werden Kaufabschlussrate, Add-to-Cart-Rate und Umsatz pro Session täglich mit den Vorwochen verglichen, so fallen Fehler in Stunden auf und nicht im Monatsbericht.",
          ],
        },
        {
          h: "Den Umzug als Conversion-Chance nutzen",
          p: [
            "Ein Nachbau des alten Shops bringt dieselben Zahlen bei neuem Wartungsvertrag. Sinnvoll ist es, die zwei bis drei größten Umsatzlecks direkt im neuen Theme zu schließen, der Aufwand dafür ist während der Migration am geringsten.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-vs-woocommerce", label: "Shopify vs. WooCommerce" },
        { to: "/cases", label: "Cases ansehen" },
        { to: "/kontakt", label: "Migration anfragen" },
      ]}
    />
  );
}
